import React, { lazy } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { gameTypes } from 'config/promoGameTypes';
import SignForm from 'client/game_signForm/Game';
import PurePrizeGameContainer from 'client/components/JoinGame/PurePrizeGameContainer';

const PsychoQuiz = lazy(() =>
    import(/* webpackChunkName: "client_psycho_quiz" */ 'client/game_psychoQuiz/Game')
);
const Quiz = lazy(() => import(/* webpackChunkName: "client_quiz" */ 'client/game_quiz/Game'));
const Survey = lazy(() =>
    import(/* webpackChunkName: "client_survey" */ 'client/game_survey/Game')
);
const Vote = lazy(() => import(/* webpackChunkName: "client_vote" */ 'client/game_vote/Game'));
const Invoice = lazy(() =>
    import(/* webpackChunkName: "client_invoice" */ 'client/game_invoice/Game')
);
const Winwheel = lazy(() =>
    import(/* webpackChunkName: "client_winwheel" */ 'client/game_winwheel/Game')
);
const PokeGame = lazy(() => import(/* webpackChunkName: "client_poke" */ 'client/game_poke/Game'));
const ScratchCard = lazy(() =>
    import(/* webpackChunkName: "client_scratch_card" */ 'client/game_scratchCard/Game')
);
const RedEnvelopeRain = lazy(() =>
    import(/* webpackChunkName: "client_red_envelop_rain" */ 'client/game_redEnvelopeRain/Game')
);
const PhotoUpload = lazy(() =>
    import(/* webpackChunkName: "client_photo_upload" */ 'client/game_photoUpload/Game')
);
const CheckIn = lazy(() =>
    import(/* webpackChunkName: "client_check_in" */ 'client/game_checkIn/Game')
);
const PhotoFrameComposite = lazy(() =>
    import(
        /* webpackChunkName: "client_photo_frame_composite" */ 'client/game_photoFrameComposite/Game'
    )
);
const Slots = lazy(() => import(/* webpackChunkName: "client_slots" */ 'client/game_slots/Game'));
const PickThePrize = lazy(() =>
    import(/* webpackChunkName: "client_pick_the_prize" */ 'client/game_pickThePrize/Game')
);
const Comment = lazy(() =>
    import(/* webpackChunkName: "client_comment" */ 'client/game_comment/Game')
);
const MemoryMatch = lazy(() =>
    import(/* webpackChunkName: "client_memory_match" */ 'client/game_memoryMatch/Game')
);
const PhotoFrameCompositeChallenge = lazy(() =>
    import(
        /* webpackChunkName: "client_photo_frame_composite_challenge" */ 'client/game_photoFrameCompositeChallenge/Game'
    )
);
const VotingContest = lazy(() =>
    import(/* webpackChunkName: "client_voting_contest" */ 'client/game_votingContest/Game')
);
const VideoUpload = lazy(() =>
    import(/* webpackChunkName: "client_video_upload" */ 'client/game_videoUpload/Game')
);
const InstagramModule = lazy(() =>
    import(/* webpackChunkName: "client_game_instagram" */ 'client/game_instagram/Game')
);
const PurchaseReward = lazy(() =>
    import(/* webpackChunkName: "client_purchase_reward" */ 'client/game_purchaseReward/Game')
);
const PointsRedeem = lazy(() =>
    import(/* webpackChunkName: "client_points_redeem" */ 'client/game_pointsRedeem/Game')
);
const SignUp = lazy(() =>
    import(/* webpackChunkName: "client_sign_up" */ 'client/game_signUp/Game')
);
const MgmShare = lazy(() =>
    import(/* webpackChunkName: "client_check_in" */ 'client/game_mgmShare/Game')
);
const MultiChallenge = lazy(() =>
    import(/* webpackChunkName: "client_check_in" */ 'client/game_multiChallenge/Game')
);
const OrderReward = lazy(() =>
    import(/* webpackChunkName: "client_order_reward" */ 'client/game_orderReward/Game')
);
const CartReward = lazy(() =>
    import(/* webpackChunkName: "client_cart_reward" */ 'client/game_cartReward/Game')
);
const MissionReward = lazy(() =>
    import(/* webpackChunkName: "client_mission_reward" */ 'client/game_missionReward/Game')
);

// 分三種 Client: 一般模組、純獎模組、好禮自由選
const Games = {
    [gameTypes.PSYCHO_QUIZ]: PsychoQuiz,
    [gameTypes.SIGN_FORM]: SignForm,
    [gameTypes.QUIZ]: Quiz,
    [gameTypes.SURVEY]: Survey,
    [gameTypes.VOTE]: Vote,
    [gameTypes.INVOICE]: Invoice,
    [gameTypes.PHOTO_UPLOAD]: PhotoUpload,
    [gameTypes.CHECK_IN]: CheckIn,
    [gameTypes.PHOTO_FRAME_COMPOSITE]: PhotoFrameComposite,
    [gameTypes.MEMORY_MATCH]: MemoryMatch,
    [gameTypes.COMMENT]: Comment,
    [gameTypes.CHALLENGE_PHOTO_FRAME_COMPOSITE]: PhotoFrameCompositeChallenge,
    [gameTypes.CONTEST_ADMIN_UPLOAD]: VotingContest,
    [gameTypes.VIDEO_UPLOAD]: VideoUpload,
    [gameTypes.INSTAGRAM]: InstagramModule,
    [gameTypes.PURCHASE_REWARD]: PurchaseReward,
    [gameTypes.SIGN_UP]: SignUp,
    [gameTypes.MGM_SHARE]: MgmShare,
    [gameTypes.MULTI_CHALLENGE]: MultiChallenge,
    [gameTypes.ORDER_REWARD]: OrderReward,
    [gameTypes.CART_REWARD]: CartReward,
    [gameTypes.MISSION_REWARD_BY_STAFF]: MissionReward
};

const PurePrizeGames = {
    [gameTypes.WIN_WHEEL]: Winwheel,
    [gameTypes.POKE_GAME]: PokeGame,
    [gameTypes.SCRATCH_CARD]: ScratchCard,
    [gameTypes.RED_ENVELOPE_RAIN]: RedEnvelopeRain,
    [gameTypes.SLOTS]: Slots,
    [gameTypes.POINTS_REDEEM]: PointsRedeem
};

export default function GameClient({ onGameFinish, onPurePrizeGameFinish, trackingData }) {
    const { type } = useSelector(s => s.promo);
    const { isPurePrizeGame } = useSelector(s => s.promoState);
    const Game = Games[type] || 'div';
    const PurePrizeGame = PurePrizeGames[type] || 'div';
    const isPickThePrize = type === gameTypes.PICK_THE_PRIZE;

    return isPickThePrize ? (
        <PickThePrize onPurePrizeGameFinish={onPurePrizeGameFinish} />
    ) : isPurePrizeGame ? (
        <PurePrizeGameContainer onPurePrizeGameFinish={onPurePrizeGameFinish}>
            {props => <PurePrizeGame {...props} />}
        </PurePrizeGameContainer>
    ) : (
        <Game onGameFinish={onGameFinish} trackingData={trackingData} />
    );
}

GameClient.propTypes = {
    onGameFinish: PropTypes.func,
    onPurePrizeGameFinish: PropTypes.func,
    trackingData: PropTypes.object
};
